import { RiShareBoxFill, RiGitBranchLine } from "react-icons/ri";
import { MdLanguage, MdDelete, MdVpnLock, MdWarning, MdError } from "react-icons/md";
import { FaGithub } from "react-icons/fa";
import { formatDate, toCamelCase, getSSLStatus } from "../utils/units";
import DeploymentLogs from "./DeploymentLogs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generateImage } from "../redux/image";
import { useWebSocketData } from "../context/WebSocketContext";
import { Link } from "react-router-dom";
import "./ProjectCard.css";

const ProjectCard = ({ project, onDelete }) => {
  const projectUrl = project.domain.startsWith("http")
    ? project.domain
    : `https://${project.domain}`;
  const dispatch = useDispatch();
  const { images, loading: imgLoading } = useSelector((state) => state.image);
  const cachedImage = useMemo(
    () => images[project.domain] || null,
    [images, project.domain],
  );
  const { permissions } = useSelector((state) => state.auth);
  const isViewOnly = permissions?.ProjectsViewOnly === true;
  const { live } = useWebSocketData();

  const statusObject = useMemo(() => {
    const filtered = live.filter(
      (e) => e.event === "project_status_check" && e.domain === project.domain,
    );

    if (filtered.length === 0) return null;

    // Sort descending by timestamp
    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return filtered[0]; // latest status object
  }, [live, project.domain]);

  const ssl = statusObject?.ssl || {};
  
  const getSSLDisplay = () => {
    const daysRemaining = ssl.sslRemainingDays;
    const computedStatus = getSSLStatus(daysRemaining);
    
    switch (computedStatus) {
      case "valid":
        return {
          icon: <MdVpnLock className="text-success" />,
          text: "Secure Connection",
          class: "ssl-valid",
          subtext: `Expires in ${daysRemaining} days`
        };
      case "expiring":
        return {
          icon: <MdWarning className="text-warning" />,
          text: "Renewal Recommended",
          class: "ssl-expiring",
          subtext: `Expires in ${daysRemaining} days`
        };
      case "critical":
        return {
          icon: <MdError className="text-danger" />,
          text: "Action Required",
          class: "ssl-critical",
          subtext: `Expiring in ${daysRemaining} days!`
        };
      case "expired":
        return {
          icon: <MdError className="text-danger" />,
          text: "Certificate Expired",
          class: "ssl-expired",
          subtext: "Site at Risk"
        };
      default:
        return {
          icon: <MdVpnLock className="text-muted" />,
          text: "SSL Status Unknown",
          class: "ssl-unknown",
          subtext: "Checking..."
        };
    }
  };

  const sslDisplay = getSSLDisplay();

  useEffect(() => {
    if (!project?.domain) return;
    // If we don't have a cached image and we're not already loading one, request it
    dispatch(generateImage(project.domain));
  }, [project.domain, dispatch]);

  return (
    <div className="col-12 col-md-4 col-xl-3 p-2">
      <div className={`project-card ${sslDisplay.class}`}>
        {/* Website Screenshot */}
        <div className="project-screenshot">
          {isViewOnly ? (
            <div className="ratio ratio-16x9">
              {!imgLoading ? (
                cachedImage ? (
                  <img
                    src={cachedImage}
                    alt={project.projectName}
                    className="screenshot-img"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/404.svg";
                    }}
                  />
                ) : (
                  <img
                    src="/404.svg"
                    alt="No Screenshot"
                    className="screenshot-img"
                  />
                )
              ) : (
                <div className="screenshot-loading">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link to={`/project/${project._id}`}>
              <div className="ratio ratio-16x9">
                {!imgLoading ? (
                  cachedImage ? (
                    <img
                      src={cachedImage}
                      alt={project.projectName}
                      className="screenshot-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/404.svg";
                      }}
                    />
                  ) : (
                    <img
                      src="/404.svg"
                      alt="No Screenshot"
                      className="screenshot-img"
                    />
                  )
                ) : (
                  <div className="screenshot-loading">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
            </Link>
          )}

          {/* Live Status Badge */}
          {statusObject && (
            <div className="live-status-badge">
              <span
                className={`status-dot ${statusObject.isHealthy ? "live" : statusObject.status === "live" ? "warning" : "offline"}`}
              ></span>
              <div className="status-details">
                <span className="status-text">
                  {toCamelCase(statusObject.status)}
                </span>
                {statusObject.responseTime && (
                  <span className="status-time">{statusObject.responseTime}ms</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Card Body */}
        <div className="project-body">
          {/* Project Name & Domain */}
          <div className="project-header">
            <h6 className="project-name">
              {project.projectName || project.domain}
            </h6>
            <a
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="project-domain"
            >
              <MdLanguage size={16} /> {project.domain}
              <RiShareBoxFill className="ms-1" />
            </a>
          </div>

          {/* Project Info Grid */}
          <div className="project-info">
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className={`info-value status-${project.status}`}>
                {toCamelCase(project.status)}
              </span>
            </div>

            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">
                {formatDate(project.createdAt, "day-shortMonth-year")}
              </span>
            </div>



            {project.city && (
              <div className="info-item">
                <span className="info-label">City</span>
                <span className="info-value">{project.city}</span>
              </div>
            )}
          </div>

          {/* GitHub Info */}
          {project.githubRepo && !isViewOnly && (
            <div className="github-info">
              <div className="github-header">
                <FaGithub size={16} />
                <span>GitHub Repository</span>
              </div>
              <div className="github-details">
                <div className="github-branch">
                  <RiGitBranchLine /> {project?.githubData?.branch || "main"}
                </div>
                <a
                  href={project.githubRepo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="github-link"
                >
                  View Repo <RiShareBoxFill />
                </a>
              </div>
              {project?.githubData?.lastCommitDate && (
                <div className="github-commit">
                  {project?.githubData?.lastCommitAvatar && (
                    <img
                      src={project.githubData.lastCommitAvatar}
                      alt={project.githubData.lastCommitAuthorName || ""}
                      className="commit-avatar"
                    />
                  )}
                  <span className="commit-date">
                    {formatDate(
                      project.githubData.lastCommitDate,
                      "day-shortMonth-year-time",
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Delete Button */}
          {!isViewOnly && (
            <button
              className="btn btn-danger"
              onClick={() => onDelete(project)}
            >
              <MdDelete size={18} /> Delete Project
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
